import { Form } from 'antd';
import { useCallback, useMemo } from 'react';

import type { ClientSubscriptionView } from '@app/components/RecordModal/components/RecordDetails/components/ClientSubscriptions/utils';
import type {
  Service,
  UseClientSubscriptionServicesProps,
} from '@app/components/RecordModal/components/RecordDetails/types';
import {
  applyClientSubscription,
  getAlreadyAddedNotification,
} from '@app/components/RecordModal/components/RecordDetails/utils/applyClientSubscription';
import { normalizeClientSubscriptionId } from '@app/components/RecordModal/components/RecordDetails/utils/recordServices';
import { getServicesDurationFields } from '@app/components/RecordModal/components/RecordDetails/utils/servicesDuration';
import { FRONT_WARNING } from '@app/constants';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

const { useWatch } = Form;

/**
 * Абонементы клиента над «Услугами» окна записи (ТЗ us/11427): клик по
 * абонементу добавляет его услуги, которые оказывает исполнитель, повторный
 * клик убирает их. Длительность записи пересчитывается так же, как при
 * правке услуг в таблице.
 */
export const useClientSubscriptionServices = ({
  form,
  serviceOptions,
}: UseClientSubscriptionServicesProps) => {
  const { showNotification } = useNotification();
  // preserve: значение из store формы, даже если список «Услуг» ещё не
  // зарегистрирован (таблица услуг — дочерний компонент).
  const services = useWatch('services', { form, preserve: true });

  const appliedClientSubscriptionIds = useMemo(
    () =>
      (services || [])
        .map((service) =>
          normalizeClientSubscriptionId(service?.clientSubscriptionId)
        )
        .filter((id): id is number => id !== null),
    [services]
  );

  const toggleClientSubscriptionHandler = useCallback(
    (subscription: ClientSubscriptionView) => {
      const { getFieldValue, setFieldsValue } = form;
      const currentServices: Service[] = getFieldValue('services') || [];
      const {
        services: nextServices,
        notProvidedNames,
        alreadyAddedNames,
      } = applyClientSubscription({
        services: currentServices,
        subscription,
        serviceOptions,
      });

      if (notProvidedNames.length) {
        showNotification(
          `Исполнитель не оказывает услуги абонемента: ${notProvidedNames.join(
            ', '
          )}`,
          FRONT_WARNING
        );
      }

      if (alreadyAddedNames.length) {
        showNotification(
          getAlreadyAddedNotification(alreadyAddedNames),
          FRONT_WARNING
        );
      }

      if (!nextServices) return;

      setFieldsValue({
        services: nextServices,
        ...getServicesDurationFields({
          nextServices,
          previousServices: currentServices,
          durationHour: getFieldValue('DurationHour') || 0,
          durationMinute: getFieldValue('DurationMinute') || 0,
          timeStart: getFieldValue('TimeStart'),
        }),
      });
    },
    [form, serviceOptions, showNotification]
  );

  return { appliedClientSubscriptionIds, toggleClientSubscriptionHandler };
};
